import React, { useEffect, useMemo, useState } from 'react';
import {
  Text,
  TouchableOpacity,
  Platform,
  View,
  StyleSheet
} from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { getReservationsCounts, getReservationsData } from '../../api/Reservation';
import { BasicLayout, CommonContainer } from '../../common/components/CustomLayout';
import { BOHButton, BOHTlbCheckbox, BOHTlbRadio, BOHTlbrSearchInput, BOHTlbrSearchPicker, BOHToolbar, renderBOHTlbDatePicker } from '../../common/components/bohtoolbar';
import { BOHTBody, BOHTD, BOHTDIconBox, BOHTH, BOHTHead, BOHTR, BOHTable } from '../../common/components/bohtable';
import { msgStr } from '../../common/constants/Message';
import { TextMediumSize, TextdefaultSize } from '../../common/constants/Fonts';
import { useAlertModal } from '../../common/hooks';
import { formatDate, formatDate2 } from '../../common/utils/DateUtils';
import { useSorting } from '../../common/hooks/useSorting';
import CalendarView from '../../common/components/agendacalander';

const ReservationsList = ({ navigation, openReservationScreen }) => {

  const { showAlert } = useAlertModal();

  const isBeachTennis = Platform.OS === 'web' && typeof window !== 'undefined' && (window.location.href.includes('demo.bodhisys.io') || window.location.href.includes('vehicle.bodhisys.io'));

  const [tableData, setTableData] = useState([]);
  const [isloading, setIsLoading] = useState(true);
  const [quantities, setQuantities] = useState({
    provisional:0,
    confirmed:0,
    checkedOut:0,
    checkedIn:0,
    canceled:0,
  });
  const [updateReservationListTrigger, setUpdateReservationListTrigger] = useState(false);
  const [periodRange, setPeriodRange] = useState<any>('');
  const initialWidths = useMemo(() => {
    const hasValue = tableData.some(item => !!item.duration_label);
    if (!hasValue) return [100, 100, 160, 160, 100, 100, 110, 110, 80, 80];
    return [100, 100, 160, 160, 100, 100, 120, 110, 110, 80, 80];
  }, [tableData]);
  const [sorting, setSorting] = useState({
    key: null,
    direction: null,
  })

  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");

  // Restore last-used view mode on mount
  useEffect(() => {
    AsyncStorage.getItem('__reservations_view_mode').then(saved => {
      if (saved === 'list' || saved === 'calendar') setViewMode(saved);
    });
  }, []);

  // Persist whenever it changes
  useEffect(() => {
    AsyncStorage.setItem('__reservations_view_mode', viewMode);
  }, [viewMode]);

  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);

  const [ searchOptions, setSearchOptions ] = useState({
    start_date : `${twoWeeksAgo.getFullYear()}-${String(twoWeeksAgo.getMonth() + 1).padStart(2, '0')}-${String(twoWeeksAgo.getDate()).padStart(2, '0')}`,
    end_date : `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`,
    customer : '',
    brand: '',
    order_number: '',
    stage: null,
    status_filter: null,
    hideWalkup: false,
    ShowOnlyManual: false,
    hideCancelled: true,
    missedPickup: false,
    delivery_method: null,
  });

  const stage = [
    'draft',
    'provisional',
    'confirmed',
    'checked out',
    'checked in',
    'canceled',
  ];

  const events = useMemo(() => {
    return tableData.map(item => {
      const categoryColors = (item.category_hex_colors || '')
        .split(',')
        .map((c: string) => c.trim())
        .filter(Boolean);
      const buildDateTime = (dateStr: string, timeStr: string | null | undefined, fallbackEnd: boolean): Date => {
        const baseDate = (() => {
          // Force local-midnight from YYYY-MM-DD to avoid UTC timezone shift.
          const m = (dateStr || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
          const d = new Date(dateStr);
          d.setHours(0, 0, 0, 0);
          return d;
        })();
        if (!timeStr) {
          if (fallbackEnd) baseDate.setHours(23, 59, 59, 999);
          return baseDate;
        }
        const tm = String(timeStr).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
        if (!tm) return baseDate;
        let h = parseInt(tm[1], 10);
        const mm = parseInt(tm[2], 10);
        const ss = tm[3] ? parseInt(tm[3], 10) : 0;
        const ampm = tm[4]?.toUpperCase();
        if (ampm === 'PM' && h < 12) h += 12;
        if (ampm === 'AM' && h === 12) h = 0;
        baseDate.setHours(h, mm, ss, 0);
        return baseDate;
      };
      const startDate = buildDateTime(item.start_date, item.start_time, false);
      const endDate = buildDateTime(item.end_date, item.end_time, true);
      return {
        id: item.id,
        title: `${item.order_number}`,
        start: startDate,
        end: endDate,
        startTime: item.start_time,
        endTime: item.end_time,
        color: item.plantation_hex_color || '#ffffff',
        categoryColors,
        customer: item.full_name,
        brand: item.brand,
        address: item.address,
        status: item.status_text,
        stage: stage[item.stage],
      };
    });
  }, [tableData]);

  useEffect(()=>{
    loadSearchOption();
  }, [])
  
  const loadSearchOption = async () => {
    const [cachedSearchOptions, cachedTimestamp] = await Promise.all([
      AsyncStorage.getItem('__search_options'),
      AsyncStorage.getItem('__search_options_timestamp')
    ]);
    if (cachedTimestamp && cachedSearchOptions &&(new Date().getTime() - parseInt(cachedTimestamp, 10)) < 600000 ) {
      // The cache key is shared with OrdersList; drop its scan filter so this
      // screen isn't invisibly filtered by an item barcode.
      const { item_barcode, ...parsed } = JSON.parse(cachedSearchOptions);
      setSearchOptions(parsed);
    } else {
      AsyncStorage.removeItem('__search_options');
    }
  }

  useEffect(()=>{
    const timeout = setTimeout(() => {
      AsyncStorage.setItem('__search_options', JSON.stringify(searchOptions))
      AsyncStorage.setItem('__search_options_timestamp', new Date().getTime().toString())
      setUpdateReservationListTrigger(true)
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchOptions])

  useEffect(() => {
    const today = new Date();
    switch (periodRange.toLowerCase()) {
      case 'today':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date()),
          end_date: formatDate(new Date()),
        });
        break;
      case 'tomorrow':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)),
          end_date: formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)),
        });
        break;
      case 'yesterday':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)),
          end_date: formatDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)),
        });
        break;
      case 'today+tomorrow':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date()),
          end_date: formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)),
        });
        break;
      case '7days':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date()),
          end_date: formatDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
        });
      break;
      case '30days':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date()),
          end_date: formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
        });
      break;
      case '6months':
        const sixMonthsAhead = new Date(today);
        sixMonthsAhead.setMonth(sixMonthsAhead.getMonth() + 6);
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date()),
          end_date: formatDate(sixMonthsAhead),
        });
      break;
      case 'next fri':
        let nextFriday = new Date(today);
        nextFriday.setDate(nextFriday.getDate() + (5 + 7 - nextFriday.getDay()) % 7);
        if (nextFriday.getTime() === today.getTime()) {
          nextFriday.setDate(nextFriday.getDate() + 7);
        }
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(nextFriday),
          end_date: formatDate(nextFriday),
        });
        break;
      case 'next sat':
        let nextSaturday = new Date(today);
        nextSaturday.setDate(nextSaturday.getDate() + (6 + 7 - nextSaturday.getDay()) % 7);
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(nextSaturday),
          end_date: formatDate(nextSaturday),
        });
        break;
      case 'next sun':
        let nextSunday = new Date(today);
        nextSunday.setDate(nextSunday.getDate() + (7 + 7 - nextSunday.getDay()) % 7);
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(nextSunday),
          end_date: formatDate(nextSunday),
        });
        break;
      case 'next mon':
        let nextMonday = new Date(today);
        nextMonday.setDate(nextMonday.getDate() + (1 + 7 - nextMonday.getDay()) % 7);
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(nextMonday),
          end_date: formatDate(nextMonday),
        });
        break;
      default:
        break;
    }
  }, [periodRange]);

  useEffect(() => {
    if (searchOptions.start_date && searchOptions.end_date) {
      if (searchOptions.start_date === formatDate(new Date()) &&
          searchOptions.end_date === formatDate(new Date())) {
        if(periodRange != 'Today') setPeriodRange('Today');
      } else if (searchOptions.start_date === formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)) &&
                  searchOptions.end_date === formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000))) {
        if(periodRange != 'Tomorrow') setPeriodRange('Tomorrow');
      } else if (searchOptions.start_date === formatDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)) &&
                  searchOptions.end_date === formatDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000))) {
        if(periodRange != 'Yesterday') setPeriodRange('Yesterday');
      } else if (searchOptions.start_date === formatDate(new Date()) &&
                  searchOptions.end_date === formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000))) {
        if(periodRange != 'Today+Tomorrow') setPeriodRange('Today+Tomorrow');
      } else if (searchOptions.start_date === formatDate(new Date()) &&
                  searchOptions.end_date === formatDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))) {
        if(periodRange != '7days') setPeriodRange('7days');
      } else if (searchOptions.start_date === formatDate(new Date()) &&
                  searchOptions.end_date === formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))) {
        if(periodRange != '30days') setPeriodRange('30days');
      } else if (searchOptions.start_date === formatDate(new Date()) &&
                  searchOptions.end_date === (() => { const d = new Date(); d.setMonth(d.getMonth() + 6); return formatDate(d); })()) {
        if(periodRange != '6months') setPeriodRange('6months');
      } else if (searchOptions.start_date == searchOptions.end_date && new Date(searchOptions.start_date).getDay() === 4 
                && (new Date(searchOptions.start_date).getTime() - new Date().getTime())/86400000 < 7) {
        if(periodRange != 'Next Fri') setPeriodRange('Next Fri');
      } else if (searchOptions.start_date == searchOptions.end_date && new Date(searchOptions.start_date).getDay() === 5 
                && (new Date(searchOptions.start_date).getTime() - new Date().getTime())/86400000 < 7) {
        if(periodRange != 'Next Sat') setPeriodRange('Next Sat');
      } else if (searchOptions.start_date == searchOptions.end_date && new Date(searchOptions.start_date).getDay() === 6 
                && (new Date(searchOptions.start_date).getTime() - new Date().getTime())/86400000 < 7) {
        if(periodRange != 'Next Sun') setPeriodRange('Next Sun');
      } else if (searchOptions.start_date == searchOptions.end_date && new Date(searchOptions.start_date).getDay() === 0 
                && (new Date(searchOptions.start_date).getTime() - new Date().getTime())/86400000 < 7) {
        if(periodRange != 'Next Mon') setPeriodRange('Next Mon');
      } else {
        if(periodRange != 'custom') setPeriodRange('custom');
      }
    }
  }, [searchOptions.start_date, searchOptions.end_date]);

  const formatTime12h = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    let h = parseInt(hours);
    const m = minutes;
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12;
    return `${h}:${m} ${ampm}`;
  };

  const changeSearchOptions = (key, val) => {
    setSearchOptions(prevOptions => ({
      ...prevOptions,
      [key]: val
    }));
  }

  useEffect(() => {
    if (updateReservationListTrigger == true) getTable();
  }, [updateReservationListTrigger]);

  const getTable = () => {
    setIsLoading(true);
    getReservationsData({searchOptions:searchOptions}, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateReservationListTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
    getReservationsCounts({searchOptions:searchOptions}, (jsonRes, status, error) => {
      const quantities = {
        provisional:0,
        confirmed:0,
        checkedOut:0,
        checkedIn:0,
        canceled:0,
      }

      if(status == 200){
        jsonRes.map(item=>{
          if(item.stage == 1) quantities.provisional = item.amounts;
          else if(item.stage == 2) quantities.confirmed = item.amounts;
          else if(item.stage == 3) quantities.checkedOut = item.amounts;
          else if(item.stage == 4) quantities.checkedIn = item.amounts;
          else if(item.stage == 5) quantities.canceled = item.amounts;
        })
      }

      setQuantities(quantities);
    })
  };
  // const { sortedData, handleSort, sorting } = useSorting('reservation', tableData, { key: 'order_number', direction: 'ascending' });

  const handleSort = (key:string) => {
    const sortsUpdate = {
      key: key,
      direction: sorting.key == key && sorting.direction == 'ascending' ? 'descending' : 'ascending'
    }
    setSorting(sortsUpdate)
  }

  const renderTableData = useMemo(() => {
        
    if(sorting.key) 
    tableData.sort((a, b) => {
      if (a[sorting.key] < b[sorting.key]) return sorting.direction === 'ascending' ? -1 : 1;
      if (a[sorting.key] > b[sorting.key]) return sorting.direction === 'ascending' ? 1 : -1;
      return 0;
    });

    const convertStageToString = (stage) => {
      switch (stage) {
        case null: case 'null': 
          return 'Draft';
        case 0: case '0': 
          return 'Draft';
        case 1: case '1': 
          return 'Provisional';
        case 2: case '2': 
          return 'Confirmed';
        case 3: case '3': 
          return 'Checked out';
        case 4: case '4': 
          return 'Checked in';
        case 5: case '5': return 'Canceled';
        default:  return 'Invalid stage';
      }
    }

    const rows = [];
    if (tableData.length > 0) {
      tableData.map((item, index) => {
        let wIndex = 0;
        rows.push(
          <BOHTR key={index}>
            <BOHTD width={initialWidths[wIndex++]}>
              <View>
                <Text>{item.order_number}</Text>
                {!!String(item.mapbox_id ?? '').trim() && item.lat != null && item.lng != null && (
                  <View style={{alignSelf:'flex-start', backgroundColor:'#004B87', borderRadius:3, paddingHorizontal:4, marginTop:2}}>
                    <Text style={{color:'white', fontSize:10, fontWeight:'bold'}}>USPS</Text>
                  </View>
                )}
              </View>
            </BOHTD>
            <BOHTD width={initialWidths[wIndex++]}>{formatDate2(new Date(item.createdAt))}</BOHTD>
            <BOHTD width={initialWidths[wIndex++]}>{item.brand}</BOHTD>
            <BOHTD width={initialWidths[wIndex++]}>{item.full_name}</BOHTD>
            <BOHTD width={initialWidths[wIndex++]}>
              <View>
                <Text>{item.start_date ? formatDate2(new Date(`${item.start_date} 00:00:00`)) : ''}</Text>
                <Text style={{color:'red', fontSize:10}}>{formatTime12h(item.start_time)}</Text>
              </View>
            </BOHTD>
            <BOHTD width={initialWidths[wIndex++]}>
              <View>
                <Text>{item.end_date ? formatDate2(new Date(`${item.end_date} 00:00:00`)) : ''}</Text>
                <Text style={{color:'red', fontSize:10}}>{formatTime12h(item.end_time)}</Text>
              </View>
            </BOHTD>
            {tableData.some(item => !!item.duration_label) && 
              <BOHTD width={initialWidths[wIndex++]}>
                <Text style={{color:'red'}}>{item.duration_label || ''}</Text>
              </BOHTD>
            }
            <BOHTD width={initialWidths[wIndex++]} textAlign={'right'}>{item?.quantity??''}</BOHTD>
            <BOHTD width={initialWidths[wIndex++]}>{convertStageToString(item.stage)}</BOHTD>
            <BOHTD width={initialWidths[wIndex++]} textAlign='center'>{item.printed?'Yes':'No'}</BOHTD>
            <BOHTDIconBox width={initialWidths[wIndex++]}>
              <TouchableOpacity
                onPress={() => {
                  openReservationScreen('Proceed Reservation', {reservationId:item.id})
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="arrow-right" color="black" />
              </TouchableOpacity>
            </BOHTDIconBox>
          </BOHTR>
        );
      });
    } else {
      <></>;
    }

    setIsLoading(false);
    return <>{rows}</>;
  }, [tableData, sorting]);

  const tableElement = useMemo(()=>{
    let wIndex = 0;
    const headers = [
      { key: "order_number", label: "Order #" },
      { key: "createdAt", label: "Created" },
      { key: "brand", label: "Brand" },
      { key: "full_name", label: "Customer" },
      { key: "start_date", label: "Start" },
      { key: "end_date", label: "End" },
      ...(tableData.some(item => !!item.duration_label) ? [{ key: "duration_label", label: "Duration" }] : []),
      { key: "quantity", label: "Qty of bikes" },
      { key: "stage", label: "Stage" },
      { key: "printed", label: "Printed" },
    ];

    return(<BOHTable isLoading={isloading}>
      <BOHTHead>
        <BOHTR>
          {/* <BOHTH width={initialWidths[wIndex++]}>{'Order #'}</BOHTH>
          <BOHTH width={initialWidths[wIndex++]}>{'Created'}</BOHTH>
          <BOHTH width={initialWidths[wIndex++]}>{'Brand'}</BOHTH>
          <BOHTH width={initialWidths[wIndex++]}>{'Customer'}</BOHTH>
          <BOHTH width={initialWidths[wIndex++]}>{'Start'}</BOHTH>
          <BOHTH width={initialWidths[wIndex++]}>{'End'}</BOHTH>
          <BOHTH width={initialWidths[wIndex++]}>{'Qty of bikes'}</BOHTH>
          <BOHTH width={initialWidths[wIndex++]}>{'Stage'}</BOHTH>
          <BOHTH width={initialWidths[wIndex++]}>{'Printed'}</BOHTH>
          */}
          {headers.map(({ key, label }) => (
            <BOHTH
              key={key}
              width={initialWidths[wIndex++]}
              isSorted={sorting.key === key}
              direction={sorting.key === key ? sorting.direction : null}
              onClick={() => handleSort(key)}
            >
              {label}
            </BOHTH>
          ))}
          <BOHTH width={initialWidths[wIndex++]}>{'Proceed'}</BOHTH> 
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {renderTableData}
      </BOHTBody>
    </BOHTable>)}, [isloading, tableData, sorting])

  return (
    <BasicLayout
      navigation={navigation}
      screenName={'Reservation List'}
    >
      <CommonContainer>
        <BOHToolbar style={{zIndex:100}}>
          <Text style={{marginRight:8, fontSize:TextdefaultSize}}>Start</Text>
          {Platform.OS == 'web' && 
            renderBOHTlbDatePicker(searchOptions.start_date, (date) => {
              const year = date.getFullYear();
              const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              changeSearchOptions('start_date', formattedDate);
          })}
          <Text style={{marginHorizontal:8, fontSize:TextdefaultSize}}>End</Text>
          {Platform.OS == 'web' && 
            renderBOHTlbDatePicker(searchOptions.end_date, (date) => {
              const year = date.getFullYear();
              const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              changeSearchOptions('end_date', formattedDate);
          })}
          <BOHTlbRadio
            label='Today'
            onPress={()=>{setPeriodRange('Today')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == 'Today'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='Tomorrow'
            onPress={()=>{setPeriodRange('Tomorrow')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == 'Tomorrow'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='Yesterday'
            onPress={()=>{setPeriodRange('Yesterday')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == 'Yesterday'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='Today+Tomorrow'
            onPress={()=>{setPeriodRange('Today+Tomorrow')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == 'Today+Tomorrow'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='7 days'
            onPress={()=>{setPeriodRange('7days')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == '7days'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='30 days'
            onPress={()=>{setPeriodRange('30days')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == '30days'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='6 months'
            onPress={()=>{setPeriodRange('6months')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == '6months'? 'checked': 'unchecked',
            }}
          />
        </BOHToolbar>
        <BOHToolbar>
          <BOHTlbRadio
            label='Next Fri'
            style={{margin:0}}
            onPress={()=>{setPeriodRange('Next Fri')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == 'Next Fri'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='Next Sat'
            onPress={()=>{setPeriodRange('Next Sat')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == 'Next Sat'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='Next Sun'
            onPress={()=>{setPeriodRange('Next Sun')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == 'Next Sun'? 'checked': 'unchecked',
            }}
          />
          <BOHTlbRadio
            label='Next Mon'
            onPress={()=>{setPeriodRange('Next Mon')}}
            RadioButtonProps={{
              value: '1',
              status: periodRange == 'Next Mon'? 'checked': 'unchecked',
            }}
          />
        </BOHToolbar>
        <BOHToolbar>
          <BOHTlbRadio
            label={`Checked In (${quantities.checkedIn})`}
            style={{margin:0}}
            onPress={()=>{
              changeSearchOptions('stage', null);
              changeSearchOptions('status_filter', 1)
            }}
            RadioButtonProps={{
              value: '1',
              status: searchOptions.status_filter == 1? 'checked': 'unchecked',
              color: '#ff4d4d',
            }}
          />
          <BOHTlbRadio
            label={`Checked Out (${quantities.checkedOut})`}
            onPress={()=>{
              changeSearchOptions('stage', null);
              changeSearchOptions('status_filter', 2)
            }}
            RadioButtonProps={{
              value: '1',
              status: searchOptions.status_filter == 2? 'checked': 'unchecked',
              color: '#ff4d4d',
            }}
          />
          <BOHTlbRadio
            label={`Provisional (${quantities.provisional})`}
            onPress={()=>{
              changeSearchOptions('stage', null);
              changeSearchOptions('status_filter', 3)
            }}
            RadioButtonProps={{
              value: '1',
              status: searchOptions.status_filter == 3? 'checked': 'unchecked',
              color: '#ff4d4d',
            }}
          />
          <BOHTlbRadio
            label={`Confirmed (${quantities.confirmed})`}
            onPress={()=>{
              changeSearchOptions('stage', null);
              changeSearchOptions('status_filter', 4)
            }}
            RadioButtonProps={{
              value: '1',
              status: searchOptions.status_filter == 4? 'checked': 'unchecked',
              color: '#ff4d4d',
            }}
          />
          <BOHTlbRadio
            label={`Canceled (${quantities.canceled})`}
            onPress={()=>{
              changeSearchOptions('stage', null);
              changeSearchOptions('status_filter', 5)
            }}
            RadioButtonProps={{
              value: '1',
              status: searchOptions.status_filter == 5? 'checked': 'unchecked',
              color: '#ff4d4d',
            }}
          />
          <BOHTlbRadio
            label={`All (${quantities.checkedIn + quantities.checkedOut + quantities.provisional + quantities.confirmed})`}
            style={{opacity: searchOptions.status_filter?1:0,}}
            onPress={()=>{
              changeSearchOptions('stage', null);
              changeSearchOptions('status_filter', null)
            }}
            RadioButtonProps={{
              value: '1',
              status: searchOptions.status_filter == null? 'checked': 'unchecked',
              color: '#ff4d4d',
            }}
          />
        </BOHToolbar>
        <BOHToolbar style={{width: '100%', justifyContent:'flex-start'}}>
          <BOHTlbrSearchInput
            boxStyle={{margin:0}}
            width={180}
            label='Customer'
            defaultValue={searchOptions.customer}
            onChangeText={(val)=>changeSearchOptions('customer', val)}
          />
          <BOHTlbrSearchInput
            width={180}
            label='Brand'
            defaultValue={searchOptions.brand}
            onChangeText={(val)=>changeSearchOptions('brand', val)}
          />
          <BOHTlbrSearchInput
            width={180}
            label='Order number'
            defaultValue={searchOptions.order_number}
            onChangeText={(val)=>changeSearchOptions('order_number', val)}
          />
          <BOHTlbrSearchPicker
            width={180}
            enabled={searchOptions.status_filter?false:true}
            items={[{label:'', value:''}, ...stage.map((item, index)=>({'label':item, 'value':index}))]}
            label="Status"
            selectedValue={searchOptions.stage || ''}
            onValueChange={val=>changeSearchOptions('stage', val)}/>
          <BOHTlbrSearchPicker
            width={150}
            items={[
              {label:'All', value:''},
              {label:'Delivery', value:'1'},
              {label:'Pickup', value:'0'},
            ]}
            label="Order type"
            selectedValue={searchOptions.delivery_method == null ? '' : String(searchOptions.delivery_method)}
            onValueChange={val=>changeSearchOptions('delivery_method', val === '' ? null : Number(val))}/>
        </BOHToolbar>
        <BOHToolbar>
          <BOHButton
            label="Create"
            onPress={()=>{
              openReservationScreen('Create Reservations');
            }}/>
          <BOHButton
            style={{marginLeft:10}}
            label={viewMode === 'list' ? "Calendar View" : "List View"}
            onPress={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
          />
          <BOHTlbCheckbox
            label={'Hide Walk-up Brands'}
            style={{marginLeft:30, marginRight:10}}
            CheckboxProps={{
              value:searchOptions.hideWalkup
            }}
            onPress={()=>{
              changeSearchOptions('hideWalkup', !searchOptions.hideWalkup);
            }}
          />
          <BOHTlbCheckbox
            label={'Missed Pickup'}
            CheckboxProps={{
              value:searchOptions.missedPickup
            }}
            onPress={()=>{
              changeSearchOptions('missedPickup', !searchOptions.missedPickup);
            }}
          />
          <BOHTlbCheckbox
            label={'Hide Cancelled'}
            CheckboxProps={{
              value:searchOptions.hideCancelled
            }}
            onPress={()=>{
              changeSearchOptions('hideCancelled', !searchOptions.hideCancelled);
            }}
          />
          <BOHTlbCheckbox
            label={'Show only manual addresses'}
            CheckboxProps={{
              value:searchOptions.ShowOnlyManual
            }}
            onPress={()=>{
              changeSearchOptions('ShowOnlyManual', !searchOptions.ShowOnlyManual);
            }}
          />
          <Text style={{marginLeft:50, fontSize:TextdefaultSize}}>{`Total #:  `}{tableData?.length??0}</Text>
        </BOHToolbar>
        {viewMode === 'list' ? tableElement : <CalendarView events={events} onEventPress={(event)=>{openReservationScreen('Proceed Reservation', {reservationId:event.id})}} />}
      </CommonContainer>
    </BasicLayout>
  );
};

export default ReservationsList;
